import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link, generatePath } from 'react-router-dom'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import { useShownHub } from 'hooks/useShownHub'
import { useApplicationStore } from 'hooks/application'
import { IFolderContents } from 'types'
import { TABLE, DOCUMENT } from 'routes'
import Input from 'components/input'
import DataTable, { dataTableColumns } from 'components/data-table'
import RowActions from 'components/data-table/rowActions'
import DateCell from 'components/data-table/dateCell'
import { Process, Spreadsheet } from 'components/icons'
import { SETTINGS_CANVAS, SettingsCard, TextLink } from 'views/settings/components/settingsCard'

/** An archived document or table, as the table lists it. */
export interface DeletedResource {
  /** Unique across both kinds, as a document and a table could share an id. */
  id: string
  publicId: string
  kind: 'document' | 'table'
  name: string
  createdAt: string
  deletedAt: string
  /** Where a table opens. */
  viewId?: string
}

const NEWEST_FIRST = [{ id: 'deletedAt', desc: true }]
// The sidebar's icon for a document or table: 14px, a little heavier, and a little lighter than the name.
const ICON = 'flex opacity-70 [&>svg]:size-3.5 [&_path[stroke]]:[stroke-width:2.4]'
// Wide enough for the longest day, so the columns hold still as rows come and go.
const DATE_COLUMN = 'w-[124px]'

export const toDeletedResource = (resource: IFolderContents): DeletedResource | null => {
  if (resource.process) {
    const { publicId, name, createdAt, deletedAt } = resource.process
    return { id: `document-${publicId}`, publicId, kind: 'document', name, createdAt, deletedAt: deletedAt ?? '' }
  }
  if (resource.table) {
    const { publicId, name, createdAt, deletedAt, defaultViewId } = resource.table
    return {
      id: `table-${publicId}`,
      publicId,
      kind: 'table',
      name,
      createdAt,
      deletedAt: deletedAt ?? '',
      viewId: defaultViewId
    }
  }
  return null
}

const pathTo = (projectId: string, resource: DeletedResource, viewId = resource.viewId) =>
  resource.kind === 'document'
    ? generatePath(DOCUMENT, { projectId, processId: resource.publicId })
    : generatePath(TABLE, { projectId, tableId: resource.publicId, viewId: viewId ?? '' })

/** What a row's Restore button needs from the page: which rows are on their way back, and how to send one. */
const Restoring = React.createContext<{ restoring: string[]; restore: (resource: DeletedResource) => void }>({
  restoring: [],
  restore: () => undefined
})

/**
 * An archived document's or table's name, with its kind's icon, in the sidebar's weight, as the same
 * names read there. Not a link: an archived document or table opens only to say it's archived, so
 * it's opened from the message once it's restored.
 */
const NameCell: React.FC<{ resource: DeletedResource }> = ({ resource }) => (
  <span className="flex max-w-[280px] items-center gap-2 font-medium">
    <span className={ICON} role="img" aria-label={resource.kind === 'document' ? 'Document' : 'Table'}>
      {resource.kind === 'document' ? <Process /> : <Spreadsheet />}
    </span>
    <span className="truncate">{resource.name}</span>
  </span>
)

const RestoreAction: React.FC<{ resource: DeletedResource }> = ({ resource }) => {
  const { restoring, restore } = useContext(Restoring)
  return (
    <RowActions
      name={resource.name}
      actions={[
        {
          label: 'Restore',
          loading: restoring.includes(resource.id),
          onSelect: () => restore(resource),
          'data-tour': 'restore-deleted-resource'
        }
      ]}
    />
  )
}

// Set once, here: a cell is a component, so columns made afresh on each render would remount every cell.
const col = dataTableColumns<DeletedResource>()
const COLUMNS = col.columns([
  col.accessor('name', {
    header: 'Name',
    sortFn: 'alphanumeric',
    cell: ({ row }) => <NameCell resource={row.original} />
  }),
  col.accessor('deletedAt', {
    header: 'Archived',
    sortFn: 'text',
    enableGlobalFilter: false,
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => <DateCell iso={getValue()} />
  }),
  col.accessor('createdAt', {
    header: 'Created',
    sortFn: 'text',
    enableGlobalFilter: false,
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => <DateCell iso={getValue()} />
  }),
  col.display({
    id: 'restore',
    header: () => <span className="sr-only">Restore</span>,
    meta: { className: 'w-0 text-right' },
    cell: ({ row }) => <RestoreAction resource={row.original} />
  })
])

/**
 * The hub's archived documents and tables, the latest archived first, to find and restore. Restoring one puts it back
 * where it was in the sidebar and takes it off the list, with a link to open it.
 */
const DeletedResources: React.FC = () => {
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const projectId = project.publicId

  const [resources, setResources] = useState<DeletedResource[]>()
  const [loadFailed, setLoadFailed] = useState(false)
  const [restoring, setRestoring] = useState<string[]>([])
  const [search, setSearch] = useState('')
  // Which load is the latest, so only it fills the list: one still on its way from before (another hub,
  // the same hub before switching away and back, or an earlier Try again) is dropped.
  const latestLoad = useRef(0)
  const stillShown = useShownHub(projectId)

  const load = () => {
    latestLoad.current += 1
    const thisLoad = latestLoad.current
    setResources(undefined)
    setLoadFailed(false)
    api
      .getProjectResources(projectId, { onlyDeleted: true })
      .then((response) => {
        if (thisLoad !== latestLoad.current) return
        setResources(
          response.data.map(toDeletedResource).filter((resource): resource is DeletedResource => resource !== null)
        )
      })
      .catch(() => {
        if (thisLoad === latestLoad.current) setLoadFailed(true)
      })
  }

  // Another hub's settings: its own list, without the last one's search.
  useEffect(() => {
    setSearch('')
    if (projectId !== '') load()
  }, [projectId])

  const restored = (resource: DeletedResource, viewId?: string) => {
    setResources((current) => current?.filter((each) => each.id !== resource.id))
    setSnackbarMessage({
      status: 'success',
      message: `Restored ${resource.name}`,
      action: (
        <Link
          to={pathTo(projectId, resource, viewId)}
          className="font-medium text-inherit underline! underline-offset-2"
        >
          Open
        </Link>
      )
    })
  }

  const restore = (resource: DeletedResource) => {
    const hub = projectId
    setRestoring((current) => [...current, resource.id])
    const request =
      resource.kind === 'document'
        ? api.restoreProcess(resource.publicId, { projectId: hub, context: { projectId: hub } }).then(() => {
            if (stillShown(hub)) restored(resource)
          })
        : api.restoreTable(resource.publicId, { context: { projectId: hub } }).then((response) => {
            if (stillShown(hub)) restored(resource, response.data.defaultViewId)
          })
    request
      .catch((error) => {
        if (stillShown(hub)) displayErrorMessage(error)
      })
      .then(() => setRestoring((current) => current.filter((id) => id !== resource.id)))
  }

  return (
    <div className={`w-full min-h-full overflow-auto ${SETTINGS_CANVAS}`}>
      <div className="flex flex-col gap-4 max-w-[960px] mx-auto">
        <SettingsCard
          title="Archived files"
          description="Documents and tables archived from this hub. Files can be restored and will appear back where they were removed."
        >
          {/* Shown while the list loads, so the table doesn't move down when it arrives; gone once there's
              nothing archived to search, or the list couldn't be loaded. */}
          {!loadFailed && resources?.length !== 0 && (
            <div className="px-4 py-3">
              <Input
                type="search"
                aria-label="Search archived documents and tables"
                placeholder="Search by name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-8 max-w-[260px]"
              />
            </div>
          )}
          <div data-tour="deleted-resources-table">
            <Restoring.Provider value={{ restoring, restore }}>
              <DataTable
                label="Archived documents and tables"
                rowLabel={(resource) => resource.name}
                columns={COLUMNS}
                data={resources}
                getRowId={(resource) => resource.id}
                initialSort={NEWEST_FIRST}
                search={search}
                empty={
                  <>
                    <div className="font-medium text-body">No archived files</div>
                    <div className="mt-0.5">Documents and tables archived from this hub will appear here.</div>
                  </>
                }
                noMatches="No archived files match your search."
                error={
                  loadFailed && (
                    <>
                      <div className="font-medium text-body">Archived files couldn&apos;t be loaded</div>
                      <div className="mt-1">
                        <TextLink onClick={load}>Try again</TextLink>
                      </div>
                    </>
                  )
                }
              />
            </Restoring.Provider>
          </div>
        </SettingsCard>
      </div>
    </div>
  )
}

export default DeletedResources
